import { Link, NavLink, useNavigate } from "react-router-dom";
import { ShoppingBag, Heart, User, Menu, Search, LogOut } from "lucide-react";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import {
  Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle,
} from "@/components/ui/sheet";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";

const CATEGORIES = [
  { slug: "sarees", label: "Sarees" },
  { slug: "lehengas", label: "Party Wear" },
  { slug: "kurtis", label: "Kurtis" },
  { slug: "dresses", label: "Dresses" },
];

const TOP_LINKS = [
  { to: "/bridal", label: "Bridal" },
  { to: "/new", label: "New Arrivals" },
  { to: "/bestsellers", label: "Bestsellers" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-warm">
      <div className="h-1 w-full bg-[#5A1618]" />
      <div className="px-6 md:px-10 lg:px-16">
        <div className="flex items-center justify-between h-24">
          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="p-2" aria-label="Menu"><Menu strokeWidth={1.2} /></button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#FDFBF7] w-[82%]">
                <SheetHeader>
                  <SheetTitle className="flex items-center">
                    <img src="https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/8b0yz6uh_ffaab141-db10-4f1b-8d78-dd8685cdd1f1.jpg"
                         alt="Riddhi" className="h-14 w-auto object-contain rounded-sm" />
                  </SheetTitle>
                </SheetHeader>
                <nav className="mt-8 flex flex-col gap-5">
                  {CATEGORIES.map((c) => (
                    <NavLink key={c.slug} to={`/shop/${c.slug}`} onClick={() => setOpen(false)}
                             className="font-serif-h text-2xl text-stone-900" data-testid={`mobile-cat-${c.slug}`}>
                      {c.label}
                    </NavLink>
                  ))}
                  {TOP_LINKS.map((l) => (
                    <NavLink key={l.to} to={l.to} onClick={() => setOpen(false)}
                             className="font-serif-h text-2xl text-stone-900">
                      {l.label}
                    </NavLink>
                  ))}
                  <div className="gold-divider my-3" />
                  <NavLink to="/about" onClick={() => setOpen(false)} className="text-sm tracking-overline uppercase">Our Story</NavLink>
                  <NavLink to="/contact" onClick={() => setOpen(false)} className="text-sm tracking-overline uppercase">Contact</NavLink>
                </nav>
              </SheetContent>
            </Sheet>
          </div>

          {/* Logo */}
          <Link to="/" className="flex-1 lg:flex-none flex items-center justify-center lg:justify-start" data-testid="logo-link">
            <img src="https://customer-assets.emergentagent.com/job_riddhi-elegance/artifacts/8b0yz6uh_ffaab141-db10-4f1b-8d78-dd8685cdd1f1.jpg"
                 alt="Riddhi — Draping the World in Elegance" className="h-16 lg:h-20 w-auto object-contain rounded-sm" />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-10 flex-1 justify-center">
            {CATEGORIES.map((c) => (
              <NavLink key={c.slug} to={`/shop/${c.slug}`} data-testid={`nav-cat-${c.slug}`}
                       className={({ isActive }) =>
                         `text-[13px] tracking-overline uppercase transition-colors ${isActive ? "text-[#D4AF37]" : "text-stone-700 hover:text-[#D4AF37]"}`}>
                {c.label}
              </NavLink>
            ))}
            {TOP_LINKS.map((l) => (
              <NavLink key={l.to} to={l.to}
                       className={({ isActive }) =>
                         `text-[13px] tracking-overline uppercase transition-colors ${isActive ? "text-[#D4AF37]" : "text-stone-700 hover:text-[#D4AF37]"}`}>
                {l.label}
              </NavLink>
            ))}
          </nav>

          {/* Right icons */}
          <div className="flex items-center gap-1 lg:gap-2">
            <button className="p-2 hidden sm:inline-flex" aria-label="Search" data-testid="search-btn"
                    onClick={() => navigate("/shop/all")}>
              <Search strokeWidth={1.2} size={20} />
            </button>
            <Link to="/wishlist" className="p-2" data-testid="wishlist-link" aria-label="Wishlist">
              <Heart strokeWidth={1.2} size={20} />
            </Link>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="p-2" aria-label="Account" data-testid="account-btn">
                  <User strokeWidth={1.2} size={20} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                {user ? (
                  <>
                    <DropdownMenuLabel className="font-normal">
                      <div className="font-medium">{user.name}</div>
                      <div className="text-xs text-stone-500">{user.email}</div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={() => navigate("/orders")} data-testid="menu-orders">My Orders</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/wishlist")}>Wishlist</DropdownMenuItem>
                    {user.role === "admin" && (
                      <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">Admin Panel</DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={logout} data-testid="menu-logout"><LogOut size={14} className="mr-2" /> Sign out</DropdownMenuItem>
                  </>
                ) : (
                  <>
                    <DropdownMenuItem onClick={() => navigate("/login")} data-testid="menu-login">Sign in</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => navigate("/register")} data-testid="menu-register">Create account</DropdownMenuItem>
                  </>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
            <Link to="/cart" className="p-2 relative" data-testid="cart-link" aria-label="Cart">
              <ShoppingBag strokeWidth={1.2} size={20} />
              {count > 0 && (
                <span className="absolute -top-0.5 -right-0.5 bg-[#D4AF37] text-white text-[10px] leading-none rounded-full h-4 min-w-4 px-1 flex items-center justify-center">
                  {count}
                </span>
              )}
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
}
